/**
 * Building an XML document as a tree and printing it.
 *
 * Exports are written as data and serialised once, rather than by pushing strings onto a
 * buffer. String-building an exporter means every branch has to remember to escape, to
 * close its tag and to indent, and the one that forgets produces a file that opens
 * everywhere except in the one program the user cares about.
 *
 * `null` children are dropped, which is what makes the exporter readable: a line can say
 * `element.tempo === null ? null : sound(...)` instead of guarding around it.
 */

/** A node being built. `text` and `children` are mutually exclusive in practice. */
export interface XmlNode {
  readonly name: string;
  readonly attrs?: Readonly<Record<string, string | number | null | undefined>>;
  readonly children?: readonly (XmlNode | null)[];
  readonly text?: string | number;
}

/** Build a node. Attributes whose value is `null` or `undefined` are left out. */
export function el(
  name: string,
  attrs?: XmlNode["attrs"],
  children?: readonly (XmlNode | null)[],
): XmlNode {
  return { name, attrs: attrs ?? {}, children: children ?? [] };
}

/** Build a node holding only text. */
export function leaf(name: string, text: string | number, attrs?: XmlNode["attrs"]): XmlNode {
  return { name, attrs: attrs ?? {}, text };
}

/** Escape the five characters that cannot appear literally in content or an attribute. */
export function escapeXml(raw: string): string {
  return raw
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&apos;");
}

export interface WriteOptions {
  /** Lines prepended before the root, e.g. an XML declaration and a doctype. */
  readonly prologue?: readonly string[];
  readonly indent?: string;
}

/** Print a tree. */
export function writeXml(root: XmlNode, options: WriteOptions = {}): string {
  const indent = options.indent ?? "  ";
  const lines = [...(options.prologue ?? [])];
  print(root, 0, indent, lines);
  return `${lines.join("\n")}\n`;
}

function printAttrs(node: XmlNode): string {
  const pairs: string[] = [];
  for (const [key, value] of Object.entries(node.attrs ?? {})) {
    if (value === null || value === undefined) continue;
    pairs.push(` ${key}="${escapeXml(String(value))}"`);
  }
  return pairs.join("");
}

function print(node: XmlNode, depth: number, indent: string, lines: string[]): void {
  const pad = indent.repeat(depth);
  const open = `${pad}<${node.name}${printAttrs(node)}`;
  const kids = (node.children ?? []).filter((kid): kid is XmlNode => kid !== null);

  if (node.text !== undefined) {
    lines.push(`${open}>${escapeXml(String(node.text))}</${node.name}>`);
    return;
  }
  if (kids.length === 0) {
    lines.push(`${open} />`);
    return;
  }

  lines.push(`${open}>`);
  for (const kid of kids) print(kid, depth + 1, indent, lines);
  lines.push(`${pad}</${node.name}>`);
}
